import { ResponseResult } from '../FieldChangeResult';
import { Coordinates, CoordinatesWithSourceInfo, IGeolocationService, SetGeolocationResponseCode } from './IGeolocationService';
import { DataFrame, Method, Status } from '../../DataFrame';
import { IExalusConnectionService } from '../IExalusConnectionService';
import { Api } from '../../Api';
import { ExalusConnectionService } from '../ExalusConnectionService';
import { DependencyContainer } from '../../DependencyContainer';
import { ControllerConfigurationService } from './ControllerConfigurationService';
import { IControllerConfigurationService } from './IControllerConfigurationService';
import { WebApiCacheService } from '../WebApi/WebApiCacheService';
import { IWebApiCacheService } from '../WebApi/IWebApiCacheService';

export class GeolocationService implements IGeolocationService {
	public static readonly ServiceName: string = "GeolocationService";

	async IsControllerGeolocationSetAsync(): Promise<boolean | Status> {		
		let req = new IsGeolocationSetRequest();
		let response = await Api.Get<IExalusConnectionService>(ExalusConnectionService.ServiceName).SendAndWaitForResponseAsync<boolean>(req, 15000, true);

		switch (response?.Status) {
			case Status.OK:
				return response?.Data as boolean;

			default:
				return response?.Status as Status;
		}
	}

	async GetCurrentGeolocationAsync(): Promise<GeolocationPosition | GeolocationPositionError> {
		return new Promise<GeolocationPosition | GeolocationPositionError>(async (resolve, reject) => {
			window.navigator.geolocation.getCurrentPosition(position => {
				resolve(position);
			}, error => {
				resolve(error);
			});
		});
	}

	async SetControllerGeolocationAsync(location: Coordinates): Promise<ResponseResult<SetGeolocationResponseCode>> {
		let req = new SetCoordinatesRequest();
		req.Data.Latitude = location.Latitude;
		req.Data.Longitude = location.Longitude;
	
		DependencyContainer.Log?.Warning(GeolocationService.ServiceName, `SetControllerGeolocationAsync() ${JSON.stringify(req)}`);
		let response = await Api.Get<IExalusConnectionService>(ExalusConnectionService.ServiceName).SendAndWaitForResponseAsync<boolean>(req, 15000, false);	
		DependencyContainer.Log?.Warning(GeolocationService.ServiceName, `${JSON.stringify(response)}`);
		DependencyContainer.Log?.Error(GeolocationService.ServiceName, `EXIT CONFIGURATION MODE WILL BE FIRED! SetControllerGeolocationAsync()`);
		Api.Get<IControllerConfigurationService>(ControllerConfigurationService.ServiceName).ExitConfigurationModeAsync();

		switch (response?.Status) {
			case Status.OK:
				const cache = Api.Get<IWebApiCacheService>(WebApiCacheService.ServiceName);
				cache.Remove(new IsGeolocationSetRequest());
				cache.Remove(new GetSetCoordinatesRequest());
				return new ResponseResult<SetGeolocationResponseCode>(SetGeolocationResponseCode.OK, "");

			case Status.NoPermissionToPerformThisOperation:
			case Status.NoPermissionsToCallGivenResource:
				return new ResponseResult<SetGeolocationResponseCode>(SetGeolocationResponseCode.NoPermissions, "");

			case Status.UserIsNotLoggedIn:
				return new ResponseResult<SetGeolocationResponseCode>(SetGeolocationResponseCode.UserIsNotLoggedIn, "");

			default:
				return new ResponseResult<SetGeolocationResponseCode>(SetGeolocationResponseCode.UnknownError, "");
		}
	}

	async GetControlllerGeolocationAsync(): Promise<CoordinatesWithSourceInfo | Status> {
		let req = new GetSetCoordinatesRequest();
		DependencyContainer.Log?.Warning(GeolocationService.ServiceName, `GetControlllerGeolocationAsync(): ${JSON.stringify(req)}`);
		let response = await Api.Get<IExalusConnectionService>(ExalusConnectionService.ServiceName).SendAndWaitForResponseAsync<CoordinatesWithSourceInfo>(req, 15000, true);
		DependencyContainer.Log?.Warning(GeolocationService.ServiceName, `${JSON.stringify(response)}`);

		switch (response?.Status) {
			case Status.OK:
				let c = new CoordinatesWithSourceInfo();
				c.IsNetworkBased = (response?.Data as CoordinatesWithSourceInfo).IsNetworkBased;
				c.Latitude = (response?.Data as CoordinatesWithSourceInfo).Latitude;
				c.Longitude = (response?.Data as CoordinatesWithSourceInfo).Longitude;
				return c;

			default:
				return response?.Status as Status;
		}
	}

	GetServiceName(): string {
		return GeolocationService.ServiceName;
	}

}

class IsGeolocationSetRequest extends DataFrame<any>
{
	constructor() {
		super();
		this.Resource = "/geolocation/isset";
		this.Method = Method.Get;
	}
}

class GetSetCoordinatesRequest extends DataFrame<any>
{
	constructor() {
		super();
		this.Resource = "/geolocation/get";
		this.Method = Method.Get;
	}
}

class SetCoordinatesRequest extends DataFrame<any>
{
	constructor() {
		super();
		this.Resource = "/geolocation/save";
		this.Method = Method.Put;
	}

	Data: Coordinates = new Coordinates();
}
